import { useGetAccountQuery } from '@/services/accounts';
import { usePostEstimatedPricingMutation } from '@/services/pricing';
import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

export interface EstimatedPricingData {
  products: any[];
  showDCComparison: boolean;
  escalationRates: number[];
  isDCPricingDifferent: boolean;
  isLoading: boolean;
  error: any;
  isFetching: boolean;
  pricingFetched: boolean;
  refetchPricing: () => void;
}

export const useGetAccountEstimatedPricing = (values?: any): EstimatedPricingData => {
  const { id } = useParams<{ id: any }>();
  const { data: accountData } = useGetAccountQuery(id);
  const [postEstimatedPricing, { data: pricingDataWithDC, isLoading: isLoadingWithDC, error: errorWithDC }] =
    usePostEstimatedPricingMutation({ fixedCacheKey: `estimatedPricing-${id}-withDC` });
  const [
    postEstimatedPricingWithoutDC,
    { data: pricingDataWithoutDC, isLoading: isLoadingWithoutDC, error: errorWithoutDC },
  ] = usePostEstimatedPricingMutation({ fixedCacheKey: `estimatedPricing-${id}-withoutDC` });

  const hasInitialFetchRun = useRef(false);
  const pricingFetched = useRef(false);
  const isFirstRender = useRef(true);

  // Initial fetch only - runs once when data is available
  useEffect(() => {
    if (hasInitialFetchRun.current) return;

    if (accountData && id && values?.systemSizeKw && values?.systemFirstYearProductionKwh) {
      hasInitialFetchRun.current = true;

      const baseParams = {
        accountId: id,
        lseId: accountData.utility.lseId,
        state: accountData.address.state,
        systemSizeKw: values.systemSizeKw,
        systemFirstYearProductionKwh: values.systemFirstYearProductionKwh,
      };

      postEstimatedPricing({
        ...baseParams,
        includeDomesticContentPricing: true,
      });

      postEstimatedPricingWithoutDC({
        ...baseParams,
        includeDomesticContentPricing: false,
      });
      pricingFetched.current = true;
    } else if (accountData && id) {
      // there is an account but no system design so we don't want to fetch on change
      hasInitialFetchRun.current = true;
    }
  }, [accountData, id, values, postEstimatedPricing, postEstimatedPricingWithoutDC]);

  // Refetch when electricalUpgradeIncluded or battery changes (after initial fetch)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!hasInitialFetchRun.current) return;
    if (!accountData || !id || !values?.systemSizeKw || !values?.systemFirstYearProductionKwh) return;

    const baseParams = {
      accountId: id,
      lseId: accountData.utility.lseId,
      state: accountData.address.state,
      systemSizeKw: values.systemSizeKw,
      systemFirstYearProductionKwh: values.systemFirstYearProductionKwh,
      ...(values?.battery && values?.battery.type && values?.battery.pricingMethod
        ? {
            battery: {
              type: values.battery.type,
              pricingMethod: values.battery.pricingMethod,
              cost: values.debouncedBatteryCost,
            },
          }
        : {}),
      electricalUpgradeIncluded: values.electricalUpgradeIncluded,
    };

    postEstimatedPricing({
      ...baseParams,
      includeDomesticContentPricing: true,
    });

    postEstimatedPricingWithoutDC({
      ...baseParams,
      includeDomesticContentPricing: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values?.electricalUpgradeIncluded,
    values?.battery?.type,
    values?.battery?.pricingMethod,
    values?.debouncedBatteryCost,
  ]);

  const products = useMemo(() => {
    if (!pricingDataWithDC || !pricingDataWithoutDC) return [];

    const combinedProducts = pricingDataWithDC.reduce((acc: any, product: any) => {
      const matchingProduct = pricingDataWithoutDC.find((p: any) => p.productId === product.productId);
      if (!matchingProduct) return acc;
      return [
        ...acc,
        {
          ...product,
          ppwWithoutDC: matchingProduct.ppwRate,
          solarOnlyEPCPaymentWithoutDC: matchingProduct.solarOnlyEPCPayment,
          totalEPCPaymentWithoutDC: matchingProduct.totalEPCPayment,
        },
      ];
    }, []);
    return combinedProducts;
  }, [pricingDataWithDC, pricingDataWithoutDC]);

  const escalationRates = useMemo(() => {
    const rates = new Set<number>();
    if (products) {
      products.forEach((product: any) => {
        if (product.escalationRate !== undefined) {
          rates.add(product.escalationRate);
        }
      });
    }
    return Array.from(rates).toSorted((a: number, b: number) => a - b);
  }, [products]);

  const showDCComparison = useMemo(() => {
    return pricingDataWithDC?.find((product: any) => product.domesticContentEnabled);
  }, [pricingDataWithDC]);

  const isDCPricingDifferent = useMemo(() => {
    const dcProduct = pricingDataWithDC?.find((product: any) => product.domesticContentEnabled);
    if (dcProduct) {
      const nonDcProduct = pricingDataWithoutDC?.find((product: any) => product.productId === dcProduct.productId);
      if (nonDcProduct) {
        return dcProduct.ppwRate !== nonDcProduct.ppwRate;
      }
    }
    return false;
  }, [pricingDataWithDC, pricingDataWithoutDC]);

  const refetchPricing = () => {
    if (accountData && id && values?.systemSizeKw && values?.systemFirstYearProductionKwh) {
      const baseParams = {
        accountId: id,
        lseId: accountData.utility.lseId,
        state: accountData.address.state,
        systemSizeKw: values.systemSizeKw,
        systemFirstYearProductionKwh: values.systemFirstYearProductionKwh,
        ...(values?.battery && values?.battery.type && values?.battery.pricingMethod
          ? {
              battery: {
                type: values.battery.type,
                pricingMethod: values.battery.pricingMethod,
                cost: values.debouncedBatteryCost,
              },
            }
          : {}),
        electricalUpgradeIncluded: values.electricalUpgradeIncluded,
      };

      postEstimatedPricing({
        ...baseParams,
        includeDomesticContentPricing: true,
      });

      postEstimatedPricingWithoutDC({
        ...baseParams,
        includeDomesticContentPricing: false,
      });
      pricingFetched.current = true;
    }
  };

  const isLoading = isLoadingWithDC || isLoadingWithoutDC;
  const error = errorWithDC || errorWithoutDC;

  return {
    escalationRates,
    products,
    isLoading,
    error,
    showDCComparison,
    isDCPricingDifferent,
    isFetching: isLoading,
    refetchPricing,
    pricingFetched: pricingFetched.current,
  };
};
